import { useDispatch, useSelector } from 'react-redux';
import { RootState, store } from '../store/store';
import Logger from '../common/logger';
import { bookingApiSlice } from '../api/booking/bookingApiSlice';
import {
    CancelBookingPayload,
    CancelSaveMode,
    CancellationFeeChoice,
    bookingWriteApiSlice,
} from '../api/booking/bookingWriteApiSlice';
import { setIsConfirmationDialog, setIsLoading } from '../store/slices/globalSlice';
import { useFetchBooking } from './fetchBookingsFlow';
import { useParams } from 'react-router';
import { setBookingDetails } from '../store/slices/bookingCreationSlice';

// Soft-cancel lives on the new (ECS) backend: POST /admin/order/:id/cancel
// (bookingWriteApiSlice). Tags stay on bookingApiSlice and are invalidated
// below, same cross-slice pattern as the archive flows.
const cancelBooking = async (
    bookingId: string,
    saveMode: CancelSaveMode,
    decline?: CancelBookingPayload['decline'],
    fee?: CancellationFeeChoice,
) => {
    // unwrap() makes a backend failure reject instead of resolving with
    // `{ error }`, so whether the cancel worked is the mutation's OWN outcome.
    // It used to be read off the truthiness of the response body, with the
    // whole success path — invalidation, re-seed, the caller's snackbar and
    // navigation — inside that `if`: a 200 the UI was then never told about.
    // Same shape as deleteBookingFlow, for the same reason.
    return store
        .dispatch(bookingWriteApiSlice.endpoints.cancelBooking.initiate({ bookingId, saveMode, decline, fee }))
        .unwrap();
};
const refetchBookingDetails = async (orderId: string) => {
    // forceRefetch: the tags are invalidated a line before this runs, and
    // without it the read can resolve straight out of the entry it just
    // invalidated — re-seeding the store with the pre-cancel booking.
    const data = await store.dispatch(
        bookingApiSlice.endpoints.getBookingDetails.initiate({ orderId }, { forceRefetch: true }),
    );
    return data;
};

export const useCancelBooking = () => {
    const dispatch = useDispatch();
    const config = useSelector(
        (state: RootState) => state.authentication.config,
    );
    const useCases = config?.services;
    const fetchBooking = useFetchBooking(useCases ?? ['']);
    const bookingId = useSelector((state: RootState) => state.bookingCreation.bookingDetails.id);
    const params = useParams<{ bookingId: string }>();
    const orderId = params.bookingId ?? '';
    // The URL param is the source of truth on a detail page (deep-link rule);
    // the Redux bookingDetails id is only populated by the venue/apartment
    // pages, so the non-room detail page would otherwise cancel with ''.
    const cancelTargetId = orderId || bookingId;

    // `saveMode` comes from which button the operator pressed in the confirm
    // dialog — there is no default here on purpose: a default would be this
    // hook deciding whether a customer hears about their cancellation, which is
    // exactly the decision the dialog exists to put in front of a human.
    //
    // `decline` (M8, #1809) turns the cancel into "Decline": same request,
    // same refresh below, a different mail and an internal reason.
    //
    // `fee` (W1-6, #3042) is the operator's charge-or-waive choice from the
    // cancellation-fee dialog, when the booking's rule charges one.
    const runCancel = async (
        saveMode: CancelSaveMode,
        decline?: CancelBookingPayload['decline'],
        fee?: CancellationFeeChoice,
    ) => {
        try {
            dispatch(setIsLoading(true));
            const result = await cancelBooking(cancelTargetId, saveMode, decline, fee);

            // Past this line the booking IS canceled server-side. Everything
            // below is about telling the UI so — never a gate on success.
            // dashboardData: a decline (M8, #1809) leaves the awaiting count.
            dispatch(bookingApiSlice.util.invalidateTags(['bookingList', 'bookingDetails', 'dashboardData']));
            // The legacy booking store: the non-room detail page reads the
            // status badge and its cancel/edit gates from that list entry, not
            // from the detail payload, so it keeps offering "Stornieren" on an
            // already-canceled booking until this lands. Its own result is not
            // a verdict on the cancel — this used to be gated on it being an
            // array, and a list that came back any other way silently swallowed
            // a successful cancellation.
            await fetchBooking();
            // cancelTargetId, not the URL param: re-seed the booking we
            // actually canceled.
            const bookingDetailsResponse = await refetchBookingDetails(cancelTargetId);
            if (bookingDetailsResponse?.data) {
                dispatch(setBookingDetails(bookingDetailsResponse.data));
            }
            // What the backend actually did with the mail, so the caller's
            // snackbar can say "storniert, E-Mail konnte nicht gesendet werden"
            // instead of reporting a mail that never left as a plain success.
            return { mailSent: result?.mailSent === true, invoiceCorrection: result?.invoiceCorrection };
        } catch (error) {
            Logger('cancelBooking').error(error);
            // Rethrow so the calling page shows its error snackbar instead of
            // treating a failed cancel as "nothing happened".
            throw error;
        } finally {
            // Closed on every outcome. Left open on the failure path, the
            // dialog sat over the page and its error snackbar, so a cancel
            // that had gone wrong looked like a click that did nothing.
            dispatch(setIsConfirmationDialog(false));
            dispatch(setIsLoading(false));
        }
    };

    return runCancel;
};
